import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import t from '../theme';

const STATUS_COLORS = {
  paid:    { color: t.green,  bg: t.greenLight,  label: 'Paid' },
  pending: { color: t.orange, bg: t.orangeLight, label: 'Pending' },
  overdue: { color: t.red,    bg: t.redLight,    label: 'Overdue' },
  refunded:{ color: t.text3,  bg: t.surface2,    label: 'Refunded' },
};

function StatusBadge({ status }) {
  const s = STATUS_COLORS[status] || STATUS_COLORS.pending;
  return (
    <span style={{ background: s.bg, color: s.color, padding: '3px 10px',
      borderRadius: '20px', fontSize: '11px', fontWeight: 600 }}>{s.label}</span>
  );
}

// ── Add Payment Modal ──────────────────────────────────────────────────────
function AddPaymentModal({ students, onClose, onSave }) {
  const [form, setForm] = useState({
    student_id: '', amount: '', description: 'Monthly tuition fee',
    due_date: '', status: 'pending', payment_method: 'manual',
  });
  const [saving, setSaving] = useState(false);
  const [err,    setErr]    = useState('');

  const save = async () => {
    setErr('');
    if (!form.student_id) return setErr('Please select a student');
    if (!form.amount || isNaN(form.amount)) return setErr('Enter a valid amount');
    setSaving(true);
    try {
      await axios.post('/api/payments', form);
      onSave();
    } catch (e) { setErr(e.response?.data?.error || 'Failed to save'); }
    finally { setSaving(false); }
  };

  return (
    <div style={t.overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={t.modal}>
        <div style={t.modalHeader}>
          <h3 style={t.modalTitle}>Add Payment Record</h3>
          <button style={t.closeBtn} onClick={onClose}>✕</button>
        </div>
        <div style={t.modalBody}>
          <div style={t.fieldGroup}>
            <label style={t.label}>Student *</label>
            <select style={t.select} value={form.student_id}
              onChange={e => setForm(f => ({ ...f, student_id: e.target.value }))}>
              <option value="">— Select Student —</option>
              {students.map(s => <option key={s.id} value={s.id}>{s.full_name}</option>)}
            </select>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <div style={t.fieldGroup}>
              <label style={t.label}>Amount (CAD) *</label>
              <input style={t.input} type="number" min="0" step="0.01"
                value={form.amount} placeholder="e.g. 150.00"
                onChange={e => setForm(f => ({ ...f, amount: e.target.value }))}/>
            </div>
            <div style={t.fieldGroup}>
              <label style={t.label}>Due Date</label>
              <input style={t.input} type="date" value={form.due_date}
                onChange={e => setForm(f => ({ ...f, due_date: e.target.value }))}/>
            </div>
          </div>
          <div style={t.fieldGroup}>
            <label style={t.label}>Description</label>
            <input style={t.input} value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              placeholder="e.g. Monthly tuition fee — April 2026"/>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <div style={t.fieldGroup}>
              <label style={t.label}>Status</label>
              <select style={t.select} value={form.status}
                onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
                <option value="pending">Pending</option>
                <option value="paid">Paid</option>
                <option value="overdue">Overdue</option>
              </select>
            </div>
            <div style={t.fieldGroup}>
              <label style={t.label}>Payment Method</label>
              <select style={t.select} value={form.payment_method}
                onChange={e => setForm(f => ({ ...f, payment_method: e.target.value }))}>
                <option value="manual">Manual / Cash</option>
                <option value="e-transfer">E-Transfer</option>
                <option value="stripe">Stripe</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>
          {err && <div style={t.errorBox}>{err}</div>}
        </div>
        <div style={t.modalFooter}>
          <button style={t.btnSecondary} onClick={onClose}>Cancel</button>
          <button style={t.btnPrimary} onClick={save} disabled={saving}>
            {saving ? 'Saving...' : 'Add Payment'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Set Recurring Fee Modal ────────────────────────────────────────────────
function FeeModal({ students, onClose, onSave }) {
  const [form, setForm] = useState({
    student_id: '', amount: '', frequency: 'monthly',
    description: 'Monthly tuition fee', next_due_date: '',
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const save = async () => {
    setErr('');
    if (!form.student_id) return setErr('Please select a student');
    if (!form.amount || isNaN(form.amount)) return setErr('Enter a valid amount');
    setSaving(true);
    try {
      await axios.post('/api/payments/fees', form);
      onSave();
    } catch (e) { setErr(e.response?.data?.error || 'Failed to save'); }
    finally { setSaving(false); }
  };

  return (
    <div style={t.overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={t.modal}>
        <div style={t.modalHeader}>
          <h3 style={t.modalTitle}>Set Recurring Fee</h3>
          <button style={t.closeBtn} onClick={onClose}>✕</button>
        </div>
        <div style={t.modalBody}>
          <div style={t.fieldGroup}>
            <label style={t.label}>Student *</label>
            <select style={t.select} value={form.student_id}
              onChange={e => setForm(f => ({ ...f, student_id: e.target.value }))}>
              <option value="">— Select Student —</option>
              {students.map(s => <option key={s.id} value={s.id}>{s.full_name}</option>)}
            </select>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <div style={t.fieldGroup}>
              <label style={t.label}>Amount (CAD) *</label>
              <input style={t.input} type="number" min="0" step="0.01"
                value={form.amount} placeholder="e.g. 150.00"
                onChange={e => setForm(f => ({ ...f, amount: e.target.value }))}/>
            </div>
            <div style={t.fieldGroup}>
              <label style={t.label}>Frequency</label>
              <select style={t.select} value={form.frequency}
                onChange={e => setForm(f => ({ ...f, frequency: e.target.value }))}>
                <option value="monthly">Monthly</option>
                <option value="weekly">Weekly</option>
                <option value="per_lesson">Per Lesson</option>
                <option value="term">Per Term</option>
              </select>
            </div>
          </div>
          <div style={t.fieldGroup}>
            <label style={t.label}>Description</label>
            <input style={t.input} value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))}/>
          </div>
          <div style={t.fieldGroup}>
            <label style={t.label}>Next Due Date</label>
            <input style={t.input} type="date" value={form.next_due_date}
              onChange={e => setForm(f => ({ ...f, next_due_date: e.target.value }))}/>
          </div>
          {err && <div style={t.errorBox}>{err}</div>}
        </div>
        <div style={t.modalFooter}>
          <button style={t.btnSecondary} onClick={onClose}>Cancel</button>
          <button style={t.btnPrimary} onClick={save} disabled={saving}>
            {saving ? 'Saving...' : 'Set Fee'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main PaymentsPage ──────────────────────────────────────────────────────
export default function PaymentsPage() {
  const { isAdmin } = useAuth();
  const [payments,  setPayments]  = useState([]);
  const [fees,      setFees]      = useState([]);
  const [students,  setStudents]  = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [tab,       setTab]       = useState('history');   // history | fees | stripe
  const [addOpen,   setAddOpen]   = useState(false);
  const [feeOpen,   setFeeOpen]   = useState(false);
  const [filter,    setFilter]    = useState('');

  const load = useCallback(async () => {
    try {
      const [pr, fr] = await Promise.all([
        axios.get('/api/payments'),
        axios.get('/api/payments/fees'),
      ]);
      setPayments(pr.data);
      setFees(fr.data);
      if (isAdmin) {
        const sr = await axios.get('/api/students');
        setStudents(sr.data);
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [isAdmin]);

  useEffect(() => { load(); }, [load]);

  const markPaid = async (id) => {
    try {
      await axios.put(`/api/payments/${id}`, { status: 'paid', paid_at: new Date().toISOString() });
      load();
    } catch (e) { console.error(e); }
  };

  const deletePayment = async (id) => {
    if (!window.confirm('Delete this payment record?')) return;
    try { await axios.delete(`/api/payments/${id}`); load(); }
    catch (e) { console.error(e); }
  };

  const deleteFee = async (id) => {
    if (!window.confirm('Remove this recurring fee?')) return;
    try { await axios.delete(`/api/payments/fees/${id}`); load(); }
    catch (e) { console.error(e); }
  };

  const fmtDate = d => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
  const fmtAmt  = a => `CA$${parseFloat(a).toFixed(2)}`;

  const shown = payments.filter(p => !filter || String(p.student_id) === filter);
  const totalPaid    = payments.filter(p => p.status === 'paid').reduce((s, p) => s + parseFloat(p.amount), 0);
  const totalPending = payments.filter(p => p.status === 'pending' || p.status === 'overdue').reduce((s, p) => s + parseFloat(p.amount), 0);

  const tabStyle = active => ({
    padding: '8px 16px', borderRadius: '8px', fontSize: '13px', fontWeight: active ? 600 : 400,
    cursor: 'pointer', border: 'none', fontFamily: t.font,
    background: active ? t.accentLight : 'none',
    color: active ? t.accent : t.text3,
    borderBottom: active ? `2px solid ${t.accent}` : '2px solid transparent',
  });

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
        marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h2 style={{ fontSize: '22px', fontWeight: 600, color: t.text, margin: 0, letterSpacing: '-0.3px' }}>
            Payments
          </h2>
          <p style={{ fontSize: '13px', color: t.text3, margin: '4px 0 0' }}>
            Track fees, payments, and billing history
          </p>
        </div>
        {isAdmin && (
          <div style={{ display: 'flex', gap: '8px' }}>
            <button style={{ ...t.btnSecondary, padding: '8px 14px', fontSize: '13px' }}
              onClick={() => setFeeOpen(true)}>+ Set Recurring Fee</button>
            <button style={t.btnPrimary} onClick={() => setAddOpen(true)}>+ Add Payment</button>
          </div>
        )}
      </div>

      {/* Summary cards — admin only */}
      {isAdmin && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
          gap: '12px', marginBottom: '24px' }}>
          {[
            { label: 'Total Collected', value: fmtAmt(totalPaid), color: t.green },
            { label: 'Outstanding',     value: fmtAmt(totalPending), color: t.orange },
            { label: 'Total Payments',  value: payments.length, color: t.text2 },
            { label: 'Active Fee Plans', value: fees.filter(f => f.active).length, color: t.accent },
          ].map(({ label, value, color }) => (
            <div key={label} style={{ ...t.card, padding: '16px 20px' }}>
              <div style={{ fontSize: '11px', color: t.text3, textTransform: 'uppercase',
                letterSpacing: '0.6px', marginBottom: '6px' }}>{label}</div>
              <div style={{ fontSize: '22px', fontWeight: 700, color }}>{value}</div>
            </div>
          ))}
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '4px', marginBottom: '20px',
        borderBottom: `1px solid ${t.border}` }}>
        {[
          { key: 'history', label: 'Payment History' },
          { key: 'fees',    label: 'Recurring Fees' },
          { key: 'stripe',  label: '💳 Stripe' },
        ].map(({ key, label }) => (
          <button key={key} style={tabStyle(tab === key)} onClick={() => setTab(key)}>{label}</button>
        ))}
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', color: t.text3, padding: '48px' }}>Loading...</div>
      ) : (
        <>
          {/* ── Payment History tab ── */}
          {tab === 'history' && (
            <div>
              {isAdmin && students.length > 0 && (
                <div style={{ marginBottom: '14px' }}>
                  <select style={{ ...t.select, width: 'auto', minWidth: '180px' }}
                    value={filter} onChange={e => setFilter(e.target.value)}>
                    <option value="">All Students</option>
                    {students.map(s => <option key={s.id} value={String(s.id)}>{s.full_name}</option>)}
                  </select>
                </div>
              )}
              {shown.length === 0 ? (
                <div style={{ ...t.card, padding: '48px', textAlign: 'center' }}>
                  <p style={{ color: t.text3, margin: 0 }}>No payment records yet.</p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {shown.map(p => (
                    <div key={p.id} style={{ ...t.card, padding: '14px 18px',
                      display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap' }}>
                      <div style={{ flex: 1, minWidth: '200px' }}>
                        <div style={{ fontSize: '14px', fontWeight: 500, color: t.text, marginBottom: '3px' }}>
                          {p.description || 'Payment'}
                        </div>
                        <div style={{ fontSize: '12px', color: t.text3 }}>
                          {p.student_name && <span>{p.student_name} · </span>}
                          {p.payment_method} · {p.due_date ? `Due ${fmtDate(p.due_date)}` : fmtDate(p.createdAt)}
                          {p.paid_at && ` · Paid ${fmtDate(p.paid_at)}`}
                        </div>
                      </div>
                      <div style={{ fontSize: '16px', fontWeight: 600, color: t.text }}>
                        {fmtAmt(p.amount)}
                      </div>
                      <StatusBadge status={p.status}/>
                      {isAdmin && (
                        <div style={{ display: 'flex', gap: '6px' }}>
                          {p.status !== 'paid' && (
                            <button style={{ background: t.greenLight, border: '1px solid rgba(76,175,125,0.3)',
                              color: t.green, padding: '5px 12px', borderRadius: '6px', fontSize: '12px',
                              fontWeight: 600, cursor: 'pointer', fontFamily: t.font }}
                              onClick={() => markPaid(p.id)}>
                              Mark Paid
                            </button>
                          )}
                          <button style={{ ...t.btnDanger, padding: '5px 10px', fontSize: '12px' }}
                            onClick={() => deletePayment(p.id)}>Del</button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── Recurring Fees tab ── */}
          {tab === 'fees' && (
            <div>
              {fees.length === 0 ? (
                <div style={{ ...t.card, padding: '48px', textAlign: 'center' }}>
                  <p style={{ color: t.text3, margin: '0 0 16px' }}>No recurring fees set up yet.</p>
                  {isAdmin && (
                    <button style={t.btnPrimary} onClick={() => setFeeOpen(true)}>+ Set First Fee</button>
                  )}
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {fees.map(f => (
                    <div key={f.id} style={{ ...t.card, padding: '14px 18px',
                      display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap' }}>
                      <div style={{ flex: 1, minWidth: '200px' }}>
                        <div style={{ fontSize: '14px', fontWeight: 500, color: t.text, marginBottom: '3px' }}>
                          {f.student_name} — {f.description}
                        </div>
                        <div style={{ fontSize: '12px', color: t.text3 }}>
                          {f.frequency.replace('_', ' ')} · Next due: {fmtDate(f.next_due_date)}
                        </div>
                      </div>
                      <div style={{ fontSize: '16px', fontWeight: 600, color: t.accent }}>
                        {fmtAmt(f.amount)}
                      </div>
                      <span style={{ background: f.active ? t.greenLight : t.surface2,
                        color: f.active ? t.green : t.text3, padding: '3px 10px',
                        borderRadius: '20px', fontSize: '11px', fontWeight: 600 }}>
                        {f.active ? 'Active' : 'Inactive'}
                      </span>
                      {isAdmin && (
                        <button style={{ ...t.btnDanger, padding: '5px 10px', fontSize: '12px' }}
                          onClick={() => deleteFee(f.id)}>Remove</button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── Stripe tab ── */}
          {tab === 'stripe' && (
            <div style={{ ...t.card, padding: '48px', textAlign: 'center' }}>
              <div style={{ fontSize: '40px', marginBottom: '16px' }}>💳</div>
              <h3 style={{ fontSize: '18px', fontWeight: 600, color: t.text, margin: '0 0 10px' }}>Stripe Payments</h3>
              <p style={{ fontSize: '14px', color: t.text3, margin: 0 }}>Coming Soon — online payment integration will be available in a future update.</p>
            </div>
          )}
        </>
      )}

      {addOpen && <AddPaymentModal students={students} onClose={() => setAddOpen(false)} onSave={() => { setAddOpen(false); load(); }}/>}
      {feeOpen && <FeeModal students={students} onClose={() => setFeeOpen(false)} onSave={() => { setFeeOpen(false); load(); }}/>}
    </div>
  );
}
